import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookingsAPI, usersAPI, slotsAPI } from '../../services/api';
import { formatDisplayName } from '../../utils/displayName';
import type { Booking, User, TrainingSlot, TrainingType, RecurrencePattern } from '../../types';
import { format, parseISO, startOfDay } from 'date-fns';

interface BookingsModalProps {
  slotId: string;
  slotTitle: string;
  onClose: () => void;
}

const BookingsModal: React.FC<BookingsModalProps> = ({ slotId, slotTitle, onClose }) => {
  const [editingBooking, setEditingBooking] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['bookings', slotId],
    queryFn: () => bookingsAPI.getSlotBookings(slotId),
  });

  const { data: slot } = useQuery({
    queryKey: ['slot', slotId],
    queryFn: () => slotsAPI.getSlot(slotId),
    enabled: editingEvent,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      bookingsAPI.updateBooking(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setEditingBooking(null);
      setNewUserId('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: bookingsAPI.deleteBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
    },
  });

  const updateSlotMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TrainingSlot> }) =>
      slotsAPI.updateSlot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slot', slotId] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setEditingEvent(false);
    },
  });

  const handleEdit = (booking: Booking) => {
    setEditingBooking(booking.id);
    setNewUserId(booking.userId);
  };

  const handleSave = (bookingId: string) => {
    if (newUserId) {
      updateMutation.mutate({ id: bookingId, userId: newUserId });
    }
  };

  const handleCancel = () => {
    setEditingBooking(null);
    setNewUserId('');
  };

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const grouped = new Map<string, Booking[]>();
    bookings.forEach((booking) => {
      const bookingDate = format(startOfDay(parseISO(booking.bookedDate)), 'yyyy-MM-dd');
      if (!grouped.has(bookingDate)) {
        grouped.set(bookingDate, []);
      }
      grouped.get(bookingDate)!.push(booking);
    });
    return grouped;
  }, [bookings]);

  // Get unique dates sorted
  const uniqueDates = useMemo(() => {
    return Array.from(bookingsByDate.keys()).sort();
  }, [bookingsByDate]);

  // Get bookings to display (filtered by date if selected)
  const displayBookings = useMemo(() => {
    if (selectedDateFilter) {
      return bookingsByDate.get(selectedDateFilter) || [];
    }
    return bookings;
  }, [bookings, bookingsByDate, selectedDateFilter]);

  // Get total unique bookings count (one per user per date)
  const totalUniqueBookings = useMemo(() => {
    return bookings.length;
  }, [bookings]);

  const handleEventSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: Partial<TrainingSlot> = {
      type: formData.get('type') as TrainingType,
      title: formData.get('title') as string,
      description: formData.get('description') as string || undefined,
      date: formData.get('date') as string,
      startTime: formData.get('startTime') as string,
      endTime: formData.get('endTime') as string,
      capacity: parseInt(formData.get('capacity') as string),
      recurrencePattern: (formData.get('recurrencePattern') as RecurrencePattern) || 'NONE',
      recurrenceEndDate: formData.get('recurrenceEndDate') as string || undefined,
      recurrenceCount: formData.get('recurrenceCount')
        ? parseInt(formData.get('recurrenceCount') as string)
        : undefined,
    };
    updateSlotMutation.mutate({ id: slotId, data });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold">
              {editingEvent ? 'Edit Event' : `Bookings for: ${slotTitle}`}
            </h3>
            {!editingEvent && bookings.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                Total: {totalUniqueBookings} bookings across {uniqueDates.length} date{uniqueDates.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!editingEvent && (
              <button
                onClick={() => setEditingEvent(true)}
                className="text-primary-600 hover:text-primary-800 font-medium text-sm"
              >
                Edit Event
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {editingEvent && slot ? (
            <form onSubmit={handleEventSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    name="type"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.type}
                  >
                    <option value="SWIMMING">Swimming</option>
                    <option value="BIKING">Biking</option>
                    <option value="RUNNING">Running</option>
                    <option value="GYM">Gym</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.title}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={new Date(slot.date).toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.startTime}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    required
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.endTime}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacity
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    required
                    min="1"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.capacity}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recurrence Pattern
                  </label>
                  <select
                    name="recurrencePattern"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.recurrencePattern || 'NONE'}
                  >
                    <option value="NONE">None (Single Event)</option>
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recurrence End Date (Optional)
                  </label>
                  <input
                    type="date"
                    name="recurrenceEndDate"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={
                      slot.recurrenceEndDate
                        ? new Date(slot.recurrenceEndDate).toISOString().split('T')[0]
                        : ''
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Occurrences (Optional)
                  </label>
                  <input
                    type="number"
                    name="recurrenceCount"
                    min="1"
                    max="365"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={slot.recurrenceCount || ''}
                    placeholder="e.g., 10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  defaultValue={slot.description || ''}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={updateSlotMutation.isPending}
                  className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateSlotMutation.isPending ? 'Saving...' : 'Save Event'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingEvent(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {isLoading ? (
                <div className="text-center text-gray-500">Loading bookings...</div>
              ) : bookings.length === 0 ? (
                <div className="text-center text-gray-500">No bookings for this event</div>
              ) : (
                <>
                  {/* Date Filter */}
                  {uniqueDates.length > 1 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filter by Date:
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setSelectedDateFilter(null)}
                          className={`px-3 py-1 rounded text-sm ${
                            selectedDateFilter === null
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          All Dates ({totalUniqueBookings})
                        </button>
                        {uniqueDates.map((date) => {
                          const dateBookings = bookingsByDate.get(date) || [];
                          return (
                            <button
                              key={date}
                              onClick={() => setSelectedDateFilter(date)}
                              className={`px-3 py-1 rounded text-sm ${
                                selectedDateFilter === date
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {format(parseISO(date), 'MMM dd, yyyy')} ({dateBookings.length})
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Grouped by Date Display */}
                  {selectedDateFilter === null && uniqueDates.length > 1 ? (
                    <div className="space-y-6">
                      {uniqueDates.map((date) => {
                        const dateBookings = bookingsByDate.get(date) || [];
                        return (
                          <div key={date} className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b">
                              <h4 className="font-semibold text-gray-800">
                                {format(parseISO(date), 'EEEE, MMMM dd, yyyy')} ({dateBookings.length} booking{dateBookings.length !== 1 ? 's' : ''})
                              </h4>
                            </div>
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    User
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Booked On
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {dateBookings.map((booking) => (
                                  <tr key={booking.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {editingBooking === booking.id ? (
                                        <select
                                          value={newUserId}
                                          onChange={(e) => setNewUserId(e.target.value)}
                                          className="border rounded px-2 py-1 text-sm"
                                        >
                                          <option value="">Select user...</option>
                                          {users.map((user: User) => (
                                            <option key={user.id} value={user.id}>
                                              {formatDisplayName(user)} ({user.role})
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <div className="text-sm font-medium text-gray-900">
                                          {booking.user ? formatDisplayName(booking.user) : 'Unknown'}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {new Date(booking.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                      {editingBooking === booking.id ? (
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => handleSave(booking.id)}
                                            className="text-primary-600 hover:text-primary-800 font-medium"
                                          >
                                            Save
                                          </button>
                                          <button
                                            onClick={handleCancel}
                                            className="text-gray-600 hover:text-gray-800 font-medium"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => handleEdit(booking)}
                                            className="text-primary-600 hover:text-primary-800 font-medium"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (window.confirm('Are you sure you want to delete this booking?')) {
                                                deleteMutation.mutate(booking.id);
                                              }
                                            }}
                                            className="text-accent-600 hover:text-accent-800 font-medium"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {!selectedDateFilter && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Date
                            </th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Booked On
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {displayBookings.map((booking) => (
                          <tr key={booking.id}>
                            {!selectedDateFilter && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {format(parseISO(booking.bookedDate), 'MMM dd, yyyy')}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">
                              {editingBooking === booking.id ? (
                                <select
                                  value={newUserId}
                                  onChange={(e) => setNewUserId(e.target.value)}
                                  className="border rounded px-2 py-1 text-sm"
                                >
                                  <option value="">Select user...</option>
                                  {users.map((user: User) => (
                                    <option key={user.id} value={user.id}>
                                      {formatDisplayName(user)} ({user.role})
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <div className="text-sm font-medium text-gray-900">
                                  {booking.user ? formatDisplayName(booking.user) : 'Unknown'}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(booking.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {editingBooking === booking.id ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSave(booking.id)}
                                    className="text-primary-600 hover:text-primary-800 font-medium"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancel}
                                    className="text-gray-600 hover:text-gray-800 font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEdit(booking)}
                                    className="text-primary-600 hover:text-primary-800 font-medium"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (window.confirm('Are you sure you want to delete this booking?')) {
                                        deleteMutation.mutate(booking.id);
                                      }
                                    }}
                                    className="text-accent-600 hover:text-accent-800 font-medium"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingsModal;
